import React, { useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion } from 'framer-motion';
import {
  Briefcase,
  MapPin,
  Clock,
  CurrencyEur,
  CalendarBlank,
  Gauge,
  ArrowsLeftRight,
  TrendUp,
  TrendDown,
} from '@phosphor-icons/react';
import { computeResults, formatEUR, FUEL_DEFAULTS } from '../lib/pendelLogic';
import { TrustBadges } from '../components/TrustBadges';
import FAQ, { FaqJsonLd } from '../components/FAQ';

const DEFAULT_OLD = {
  distance_km: 15,
  office_days: 4,
  minutes_one_way: 25,
  fuel_type: 'diesel',
  fuel_price: FUEL_DEFAULTS.diesel,
  consumption: 6.5,
  net_salary: 2800,
};

const DEFAULT_NEW = {
  distance_km: 38,
  office_days: 4,
  minutes_one_way: 50,
  fuel_type: 'diesel',
  fuel_price: FUEL_DEFAULTS.diesel,
  consumption: 6.5,
  net_salary: 3300,
};

export default function JobwechselPage() {
  const [oldJob, setOldJob] = useState(DEFAULT_OLD);
  const [newJob, setNewJob] = useState(DEFAULT_NEW);

  const oldResult = useMemo(() => computeResults(oldJob), [oldJob]);
  const newResult = useMemo(() => computeResults(newJob), [newJob]);

  const net_diff_month = Number(newJob.net_salary || 0) - Number(oldJob.net_salary || 0);
  const commute_diff_month = newResult.monthly_cost - oldResult.monthly_cost;
  const time_diff_hours_year = newResult.annual_hours - oldResult.annual_hours;
  const real_advantage_month = net_diff_month - commute_diff_month;
  const real_advantage_year = real_advantage_month * 12;

  // 3-stage verdict
  let verdict;
  let verdictColor;
  let verdictHeadline;
  if (real_advantage_month >= 250) {
    verdict = 'green';
    verdictColor = '#2A9D8F';
    verdictHeadline = `Der Wechsel lohnt sich klar: ${formatEUR(real_advantage_month)} mehr pro Monat.`;
  } else if (real_advantage_month >= 50) {
    verdict = 'yellow';
    verdictColor = '#F4A261';
    verdictHeadline = `Der Wechsel bringt dir ${formatEUR(real_advantage_month)} mehr pro Monat — der Gewinn ist überschaubar. Prüfe Lebenszeit und Stress.`;
  } else if (real_advantage_month >= -50) {
    verdict = 'yellow';
    verdictColor = '#F4A261';
    verdictHeadline = `Der Gehaltsvorteil wird größtenteils durch den längeren Arbeitsweg aufgezehrt.`;
  } else {
    verdict = 'red';
    verdictColor = '#E63946';
    verdictHeadline = `Vorsicht: Trotz höherem Brutto verlierst du voraussichtlich ${formatEUR(Math.abs(real_advantage_month))} pro Monat durch die Pendelkosten.`;
  }

  const recommendations = buildJobRecommendations({
    net_diff_month,
    commute_diff_month,
    real_advantage_month,
    time_diff_hours_year,
    oldJob,
    newJob,
  });

  return (
    <main data-testid="jobwechsel-page">
      <Helmet>
        <title>Jobwechsel-Rechner 2025: Lohnt sich der neue Job? | PendelPilot</title>
        <meta
          name="description"
          content="Jobwechsel-Rechner: Vergleicht aktuelles vs. neues Netto-Gehalt mit den Pendelkosten beider Jobs. Sofort sichtbar: Was bleibt netto wirklich übrig?"
        />
        <link rel="canonical" href="/jobwechsel-rechner" />
      </Helmet>
      <FaqJsonLd />

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 pt-16 pb-10">
        <div className="text-xs uppercase tracking-widest text-neutral-500">Jobwechsel-Rechner</div>
        <h1
          data-testid="jw-h1"
          className="font-display text-5xl md:text-7xl tracking-tighter mt-2 max-w-4xl leading-[0.95]"
        >
          Lohnt sich der neue Job — <span className="italic font-medium">wirklich?</span>
        </h1>
        <p className="mt-5 text-lg text-neutral-700 max-w-2xl leading-relaxed">
          Vergleiche dein aktuelles Netto-Gehalt mit dem neuen Angebot — und zieh die echten Pendelkosten beider Jobs ab. Sieh in 30 Sekunden, was netto wirklich übrig bleibt.
        </p>
        <TrustBadges className="mt-6" />
      </section>

      {/* Two-column comparison */}
      <section className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 grid-border" data-testid="jw-compare-grid">
          <JobColumn
            title="Dein aktueller Job"
            color="neutral"
            job={oldJob}
            result={oldResult}
            onChange={setOldJob}
            testIdPrefix="jw-old"
          />
          <JobColumn
            title="Dein neues Angebot"
            color="dark"
            job={newJob}
            result={newResult}
            onChange={setNewJob}
            testIdPrefix="jw-new"
          />
        </div>
      </section>

      {/* Verdict */}
      <section className="max-w-7xl mx-auto px-6 md:px-12 mt-10">
        <motion.div
          key={Math.round(real_advantage_month)}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          className="p-8 md:p-12 grid-border text-white"
          style={{ backgroundColor: verdictColor }}
          data-testid="jw-verdict-box"
        >
          <div className="flex items-center gap-3 text-xs uppercase tracking-widest text-white/80">
            <ArrowsLeftRight size={16} weight="bold" /> Klares Fazit
          </div>
          <p
            data-testid="jw-verdict-text"
            className="font-display text-3xl md:text-5xl mt-3 leading-tight max-w-4xl"
          >
            {verdictHeadline}
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-10">
            <VerdictStat
              label="Mehr Netto / Monat"
              value={formatEUR(net_diff_month)}
              positive={net_diff_month >= 0}
              testId="jw-net-diff"
            />
            <VerdictStat
              label="Mehr Pendelkosten / Monat"
              value={formatEUR(commute_diff_month)}
              positive={commute_diff_month <= 0}
              testId="jw-commute-diff"
            />
            <VerdictStat
              label="Tatsächlicher Vorteil"
              value={formatEUR(real_advantage_month)}
              sub={`${real_advantage_year >= 0 ? '+' : ''}${formatEUR(real_advantage_year)} / Jahr`}
              positive={real_advantage_month >= 0}
              testId="jw-real-advantage"
            />
          </div>

          <div className="mt-8 pt-6 border-t border-white/30 text-sm text-white/80">
            Zusätzlich: {time_diff_hours_year >= 0 ? `+${Math.round(time_diff_hours_year)}` : `${Math.round(time_diff_hours_year)}`} Stunden Lebenszeit pro Jahr im Auto (bei gleicher Bürotag-Anzahl).
          </div>
        </motion.div>
      </section>

      {/* Recommendations */}
      {recommendations.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 md:px-12 mt-16">
          <div className="flex items-end justify-between">
            <h2 className="font-display text-3xl md:text-4xl tracking-tighter">Konkrete Empfehlungen</h2>
            <div className="hidden md:block h-px flex-1 bg-neutral-200 mx-6 mb-2" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 mt-6 grid-border" data-testid="jw-recs">
            {recommendations.map((r, i) => (
              <div
                key={i}
                data-testid={`jw-rec-${i}`}
                className="p-6 md:p-8 border-neutral-200 [&:not(:last-child)]:border-b md:[&:nth-child(odd)]:border-r"
              >
                <div className="font-mono text-xs text-neutral-500">EMPFEHLUNG {String(i + 1).padStart(2, '0')}</div>
                <div className="font-display text-xl mt-2">{r.title}</div>
                <p className="text-sm text-neutral-700 mt-2 leading-relaxed">{r.detail}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Explainer */}
      <section className="max-w-3xl mx-auto px-6 md:px-12 mt-24">
        <h2 className="font-display text-3xl md:text-4xl tracking-tighter">Wie wird gerechnet?</h2>
        <p className="mt-4 text-neutral-700 leading-relaxed">
          Wir nehmen dein angegebenes Netto-Gehalt für beide Jobs (Brutto-Netto-Rechnung machst du am besten kurz beim Steuer-Tool deiner Wahl). Davon ziehen wir die echten Pendelkosten ab — also Sprit, Verschleiß, Wertverlust und anteilige Versicherung. Der Verschleiß-Faktor liegt bei 0,12 € pro Kilometer (ADAC-Vollkosten-Mittelwert für Pendler-PKW).
        </p>
        <p className="mt-4 text-neutral-700 leading-relaxed">
          Wichtig: Die reine Brutto-Differenz täuscht oft. 300 € mehr Brutto bedeuten meist nur 170–200 € mehr Netto. Wer dann 25 km weiter pendelt, gibt schnell 200–300 € pro Monat extra fürs Auto aus. Aus dem vermeintlichen Gewinn wird im schlimmsten Fall ein Verlust.
        </p>

        <h3 className="font-display text-2xl mt-10">Was unser Rechner NICHT abbildet</h3>
        <ul className="list-disc pl-6 mt-3 text-neutral-700 space-y-2 leading-relaxed">
          <li>Steuerliche Effekte über die Pendlerpauschale (mindern dein zu versteuerndes Einkommen leicht)</li>
          <li>Unterschiede in Sozialleistungen (BAV, JobRad, Zuschüsse)</li>
          <li>Karriere-Perspektive — den langfristigen Wert eines Jobs kann kein Rechner abbilden</li>
          <li>Lebensqualität, Stress, Kollegen — wichtige Faktoren, die du selbst gewichten musst</li>
        </ul>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12">
        <FAQ />
      </section>
    </main>
  );
}

function JobColumn({ title, color, job, result, onChange, testIdPrefix }) {
  const dark = color === 'dark';
  const update = (k, v) => onChange({ ...job, [k]: v });

  return (
    <div className={dark ? 'bg-neutral-900 text-white' : 'bg-white'} data-testid={testIdPrefix}>
      <div className={`p-6 md:p-8 ${dark ? 'border-b border-white/20' : 'border-b border-neutral-200'}`}>
        <div className={`inline-flex items-center gap-2 text-xs uppercase tracking-widest ${dark ? 'text-white/70' : 'text-neutral-500'}`}>
          <Briefcase size={14} weight="bold" /> {dark ? 'Neu' : 'Aktuell'}
        </div>
        <h3 className="font-display text-3xl md:text-4xl tracking-tighter mt-2">{title}</h3>
      </div>

      <div className="grid grid-cols-2 gap-0">
        <JobField
          icon={<CurrencyEur size={16} />}
          label="Netto / Monat"
          right="€"
          dark={dark}
          value={job.net_salary}
          onChange={(v) => update('net_salary', v)}
          testId={`${testIdPrefix}-net`}
        />
        <JobField
          icon={<CalendarBlank size={16} />}
          label="Bürotage / Woche"
          right="Tage"
          dark={dark}
          value={job.office_days}
          onChange={(v) => update('office_days', v)}
          max={7}
          testId={`${testIdPrefix}-office`}
        />
        <JobField
          icon={<MapPin size={16} />}
          label="Strecke (einfach)"
          right="km"
          dark={dark}
          value={job.distance_km}
          onChange={(v) => update('distance_km', v)}
          testId={`${testIdPrefix}-distance`}
        />
        <JobField
          icon={<Clock size={16} />}
          label="Fahrzeit (einfach)"
          right="Min."
          dark={dark}
          value={job.minutes_one_way}
          onChange={(v) => update('minutes_one_way', v)}
          testId={`${testIdPrefix}-minutes`}
        />
        <JobField
          icon={<Gauge size={16} />}
          label="Verbrauch"
          right="L/100km"
          dark={dark}
          value={job.consumption}
          onChange={(v) => update('consumption', v)}
          step={0.1}
          testId={`${testIdPrefix}-cons`}
        />
        <div className={`p-5 ${dark ? 'border-b border-l border-white/20' : 'border-b border-l border-neutral-200'}`}>
          <div className={`text-xs uppercase tracking-widest ${dark ? 'text-white/60' : 'text-neutral-500'}`}>Kraftstoff</div>
          <div className="mt-3 inline-flex border border-current/30">
            {['diesel', 'e5', 'e10'].map((ft) => (
              <button
                key={ft}
                onClick={() => update('fuel_type', ft)}
                data-testid={`${testIdPrefix}-fuel-${ft}`}
                className={`px-2 h-8 text-[10px] uppercase tracking-widest transition-colors ${
                  job.fuel_type === ft
                    ? dark
                      ? 'bg-white text-neutral-900'
                      : 'bg-neutral-900 text-white'
                    : ''
                }`}
              >
                {ft === 'diesel' ? 'Diesel' : ft.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className={`p-6 md:p-8 ${dark ? 'border-t border-white/20' : 'border-t border-neutral-200'}`}>
        <div className={`text-xs uppercase tracking-widest ${dark ? 'text-white/60' : 'text-neutral-500'}`}>Pendelkosten pro Monat</div>
        <div className="font-display text-4xl md:text-5xl mt-1 tnum" data-testid={`${testIdPrefix}-monthly`}>
          {formatEUR(result.monthly_cost)}
        </div>
        <div className={`text-xs mt-2 ${dark ? 'text-white/70' : 'text-neutral-600'}`}>
          {Math.round(result.annual_hours)} h Lebenszeit / Jahr · {formatEUR(result.annual_cost)} / Jahr
        </div>
      </div>
    </div>
  );
}

function JobField({ icon, label, right, dark, value, onChange, testId, ...rest }) {
  return (
    <div
      className={`p-5 ${dark ? 'border-b border-white/20 [&:nth-child(2n)]:border-l [&:nth-child(2n)]:border-l-white/20' : 'border-b border-neutral-200 [&:nth-child(2n)]:border-l'}`}
    >
      <label className={`text-xs uppercase tracking-widest inline-flex items-center gap-1.5 ${dark ? 'text-white/60' : 'text-neutral-500'}`}>
        <span>{icon}</span> {label}
      </label>
      <div className="mt-1 flex items-baseline gap-2">
        <input
          type="number"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          data-testid={testId}
          className="w-full text-3xl font-display tracking-tight bg-transparent outline-none"
          {...rest}
        />
        <span className={`font-mono text-xs ${dark ? 'text-white/60' : 'text-neutral-500'}`}>{right}</span>
      </div>
    </div>
  );
}

function VerdictStat({ label, value, sub, positive, testId }) {
  const Icon = positive ? TrendUp : TrendDown;
  return (
    <div data-testid={testId}>
      <div className="text-xs uppercase tracking-widest text-white/80 inline-flex items-center gap-1">
        <Icon size={12} weight="bold" /> {label}
      </div>
      <div className="font-display text-3xl md:text-4xl mt-2 tnum">{value}</div>
      {sub && <div className="text-xs text-white/80 mt-1">{sub}</div>}
    </div>
  );
}

function buildJobRecommendations({ net_diff_month, commute_diff_month, real_advantage_month, time_diff_hours_year, oldJob, newJob }) {
  const recs = [];

  // Distance significantly increased
  if (newJob.distance_km - oldJob.distance_km >= 15) {
    recs.push({
      title: 'Verhandle Homeoffice mit',
      detail: `Schon 1 Tag Homeoffice pro Woche im neuen Job senkt deine zusätzlichen Pendelkosten um ca. 25%. Bei deinem Profil entspricht das etwa ${formatEUR((commute_diff_month / Math.max(newJob.office_days, 1)))} weniger Mehrkosten pro Monat.`,
    });
  }

  // Time loss is significant
  if (time_diff_hours_year >= 80) {
    recs.push({
      title: 'Lebenszeit einrechnen',
      detail: `Du verlierst zusätzlich ${Math.round(time_diff_hours_year)} h Lebenszeit pro Jahr — das sind über ${(time_diff_hours_year / 40).toFixed(1)} Arbeitswochen. Frag dich ehrlich, ob das den Gehaltsvorteil aufwiegt.`,
    });
  }

  // Net advantage is borderline
  if (real_advantage_month >= -50 && real_advantage_month < 250) {
    recs.push({
      title: 'Andere Benefits prüfen',
      detail: 'Der finanzielle Unterschied ist überschaubar. Prüfe Zusatzleistungen: betriebliche Altersvorsorge, JobRad, Fahrtkostenzuschuss, Weiterbildung. Da kann der echte Vorteil oft deutlich höher sein.',
    });
  }

  // Big income jump but kills by commute
  if (net_diff_month >= 300 && real_advantage_month < 100) {
    recs.push({
      title: 'Achtung: Versteckter Verlust',
      detail: 'Auf dem Papier ein Karriere-Schritt, in Wirklichkeit kaum besser. Verhandle entweder mehr Brutto oder einen näheren Standort — sonst wechselst du für nichts.',
    });
  }

  // Clear win
  if (real_advantage_month >= 250) {
    recs.push({
      title: 'Solide Entscheidungsgrundlage',
      detail: 'Finanziell lohnt sich der Wechsel klar. Wichtig bleibt: Bauch entscheidet auch mit. Stimmen Team, Aufgaben und Perspektive?',
    });
  }

  // Commute longer in current job (rare case)
  if (oldJob.distance_km >= newJob.distance_km + 10) {
    recs.push({
      title: 'Doppelter Gewinn',
      detail: `Kürzerer Arbeitsweg UND mehr Netto. Solche Kombi-Vorteile sind selten — wenn das Angebot inhaltlich passt, kaum Grund zu zögern.`,
    });
  }

  return recs.slice(0, 4);
}
